import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useRegistrationState } from '~/hooks/registration/useRegistrationStore';
import { currentDoseState } from '~/state/atoms/dose/doseAtoms';
import { imageListVersionState } from '~/state/atoms/image/imageAtoms';
import { currentStructureSetIdState } from '~/state/atoms/structureSet/structureSetAtoms';

export interface ExportableItems {
    images: Image3D[];
    structureSets: StructureSet[];
    doses: DoseMatrix[];
    registrations: RigidRegistration[];
    notExportedCount: number;
}

/**
 * Returns exportable items (new or previously exported)
 * and the count of items not yet exported.
 */
export default function useExportableItems(): ExportableItems {
    const patient = useSelectedPatient();
    // Subscribe to reactive state that changes when items are
    // added/removed/modified so the memo recalculates.
    const imageListVersion = useRecoilValue(imageListVersionState);
    const currentStructureSetId = useRecoilValue(currentStructureSetIdState);
    const currentDose = useRecoilValue(currentDoseState);
    const registrationState = useRegistrationState();

    return useMemo(() => {
        const images =
            patient?.images.filter((img) => img.isNew || img.isExported) ?? [];
        const structureSets =
            patient?.structureSets.filter((ss) => ss.isNew || ss.isExported) ??
            [];
        const doses =
            patient?.doses.filter((dose) => dose.isNew || dose.isExported) ??
            [];
        const registrations =
            patient?.registrations.filter(
                (reg) => reg.isNew || reg.isExported
            ) ?? [];

        const allExportableItems = [
            ...images,
            ...structureSets,
            ...doses,
            ...registrations,
        ];
        const notExportedCount = allExportableItems.filter(
            (item) => item.isExported === false
        ).length;

        return {
            images,
            structureSets,
            doses,
            registrations,
            notExportedCount,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        patient,
        imageListVersion,
        currentStructureSetId,
        currentDose,
        registrationState,
    ]);
}
